import { Component, Input, OnInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { filter, first, map, switchMap, tap } from "rxjs/operators";
import { IPerson, UserId } from "src/app/interfaces";
import { LogClass, Logger, LogMethod } from "src/app/logger";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";
import { NavigationService } from "src/app/services";
import { PersonFacade } from "src/app/services/person.facade";
import { UserFacade } from "src/app/user/services/user.facade";
import { Substitute } from "../../models/substitute";
import { AbstractSubstituteService } from "../../substitute.service";

@LogClass({ source: "Substitute editor" })
@Component({
    selector: "substitute-editor",
    templateUrl: "./shared-edit.component.html",
    standalone: false
})
export class SharedEditComponent implements OnInit {
  private logger: Logger;
  @Input()
  public userId: UserId;
  public userName: string;
  public isLoading = true;
  public formGroup: FormGroup = new FormGroup({});

  constructor(
    private service: AbstractSubstituteService,
    private navigate: NavigationService,
    private userFacade: UserFacade,
    private personaFacade: PersonFacade,
    private notificationFacade: NotificationFacade
  ) {
    this.createFormGroup();
  }

  @LogMethod()
  ngOnInit(): void {
    this.service
      .get(this.userId)
      .pipe(
        filter((sub) => {
          if (!sub) {
            this.isLoading = false;
          }

          return !!sub;
        }),
        tap((sub) => this.formGroupSetValue(sub)),
        switchMap((sub) => this.userFacade.getUser(sub.deputyId)),
        switchMap((user) => this.personaFacade.getPersonByLogin(user.login)),
        filter((u) => !!u),
        first()
      )
      .subscribe((user) => {
        this.formGroupSetDeputyValue(user);
        this.isLoading = false;
      });

    this.userFacade
      .getUser(this.userId)
      .pipe(
        filter((user) => !!user),
        map((user) => user.displayName),
        first()
      )
      .subscribe((name) => {
        this.userName = name;
      });
  }

  private formGroupSetValue(sub: Substitute): void {
    this.formGroup.patchValue({
      startDate: sub.startDate,
      endDate: sub.endDate
    });
  }

  private formGroupSetDeputyValue(deputy: IPerson): void {
    this.formGroup.patchValue({
      deputy: deputy
    });
  }

  @LogMethod()
  public save(): void {
    this.formGroup.markAllAsTouched();
    if (!this.formGroup.valid) {
      return;
    }
    this.isLoading = true;
    this.userFacade
      .getUserByLogin((this.formGroup.value.deputy as IPerson).login)
      .pipe(
        map((u) => u.id),
        switchMap((deputyId) =>
          this.service.save({
            userId: this.userId,
            deputyId: deputyId,
            startDate: this.formGroup.value.startDate,
            endDate: this.formGroup.value.endDate
          })
        ),
        first(),
        tap((sub) => this.formGroupSetValue(sub))
      )
      .subscribe(
        () => {
          this.notificationFacade.success(
            "substitute.component.user.save.success"
          );
          this.goBack();
        },
        () =>
          this.notificationFacade.showError(
            "substitute.component.user.save.fail"
          )
      );
  }

  private goBack() {
    this.navigate.back();
  }

  @LogMethod()
  public abort(): void {
    this.goBack();
  }

  @LogMethod()
  public delete(): void {
    this.isLoading = true;
    this.service
      .delete(this.userId)
      .pipe(
        first(),
        tap(() =>
          this.formGroup.setValue({
            deputy: null,
            startDate: null,
            endDate: null
          })
        )
      )
      .subscribe(
        () => {
          this.notificationFacade.success(
            "substitute.component.user.delete.success"
          );
          this.goBack();
        },
        () =>
          this.notificationFacade.showError(
            "substitute.component.user.delete.fail"
          )
      );
  }

  private validateDate(formGroup: FormGroup): ValidationErrors {
    if (!formGroup.value.endDate) {
      return null;
    }

    if (!formGroup.value.startDate) {
      return null;
    }

    const startDate = formGroup.value.startDate as Date;
    startDate.setHours(0, 0, 0, 0);
    const endDate = formGroup.value.endDate as Date;
    endDate.setHours(0, 0, 0, 0);

    const invalid = startDate > endDate;
    if (invalid) {
      formGroup.controls.endDate.setErrors({ range: true });
      return null;
    }

    formGroup.controls.endDate.setErrors(null);
    return null;
  }

  public createFormGroup() {
    this.formGroup = new FormGroup({
      deputy: new FormControl(false, [Validators.required]),
      startDate: new FormControl(null, [Validators.required]),
      endDate: new FormControl(null)
    });
    this.formGroup.addValidators(this.validateDate);
    this.formGroup.updateValueAndValidity();
  }
}
